import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTransfers,
  fetchTransfer,
  createTransfer,
  updateTransferStatus,
  fetchItemTransfers,
} from '../services/inventoryService';

/**
 * @typedef {Object} Transfer
 * @property {string} id - Transfer ID
 * @property {string} status - Transfer status
 * @property {string} createdAt - Creation timestamp
 * @property {string} updatedAt - Last update timestamp
 */

/**
 * Hook to fetch all transfers
 * @returns {import('@tanstack/react-query').UseQueryResult<Transfer[], Error>}
 */
export function useTransfers() {
  return useQuery({
    queryKey: ['inventory', 'transfers'],
    queryFn: fetchTransfers,
  });
}

/**
 * Hook to fetch transfers for a specific item
 * @param {string} itemId - ID of the item to fetch transfers for
 * @returns {import('@tanstack/react-query').UseQueryResult<Transfer[], Error>}
 */
export function useItemTransfers(itemId) {
  return useQuery({
    queryKey: ['inventory', 'transfers', 'item', itemId],
    queryFn: () => fetchItemTransfers(itemId),
    enabled: !!itemId,
  });
}

/**
 * Hook to fetch a single transfer by ID
 * @param {string} id - ID of the transfer to fetch
 * @returns {import('@tanstack/react-query').UseQueryResult<Transfer, Error>}
 */
export function useTransfer(id) {
  return useQuery({
    queryKey: ['inventory', 'transfers', id],
    queryFn: () => fetchTransfer(id),
  });
}

/**
 * Hook to create a new transfer
 * @returns {import('@tanstack/react-query').UseMutationResult<Transfer, Error, Omit<Transfer, 'id' | 'status' | 'createdAt' | 'updatedAt'>>}
 */
export function useCreateTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createTransfer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'metrics'] });
    },
  });
}

/**
 * Hook to update a transfer's status
 * @param {string} id - ID of the transfer to update
 * @returns {import('@tanstack/react-query').UseMutationResult<Transfer, Error, string>}
 */
export function useUpdateTransferStatus(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status) => updateTransferStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'metrics'] });
    },
  });
} 